import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchProviderStatus, UNKNOWN_STATUS } from '../services/providerStatusService';

const ProviderStatusContext = createContext(null);

const POLL_MS = 60 * 1000;
// Un fallo suelto de red (un 429 del limitador, un timeout) no puede apagar al instante un estado
// conocido hace menos de esto: sería veraz respecto al dato pero engañoso respecto al servicio.
const STALE_GRACE_MS = 3 * 60 * 1000;

/**
 * Estado del proveedor LLM (#1722). Polling, no WebSocket: los visitantes sin sesión no tienen
 * socket y cada `useUserSocket()` abre uno propio. Se monta por ENCIMA de AuthProvider para que
 * funcione en /login y en /status. Se pausa con la pestaña oculta y consulta al volver.
 */
export const ProviderStatusProvider = ({ children }) => {
  const [data, setData] = useState(UNKNOWN_STATUS);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const mountedRef = useRef(true);
  const seqRef = useRef(0);
  const lastGoodRef = useRef({ data: null, at: 0 });

  const refresh = useCallback(async () => {
    // Respuestas fuera de orden (sondeo vs. «Actualizar»): solo cuenta la última pedida.
    const seq = ++seqRef.current;
    const next = await fetchProviderStatus();
    if (!mountedRef.current || seq !== seqRef.current) return;

    if (next === UNKNOWN_STATUS) {
      // La consulta falló (no es un `unknown` que el backend haya dicho): se conserva lo último
      // bueno durante un margen, y pasado este se cae a `unknown`.
      const good = lastGoodRef.current;
      if (good.data && Date.now() - good.at < STALE_GRACE_MS) {
        setLoading(false);
        return;
      }
    } else {
      lastGoodRef.current = { data: next, at: Date.now() };
    }
    setData(next);
    setLastChecked(Date.now());
    setLoading(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

    if (visible()) refresh();
    const timer = setInterval(() => { if (visible()) refresh(); }, POLL_MS);
    const onVisibility = () => { if (visible()) refresh(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      mountedRef.current = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({ ...data, loading, lastChecked, refresh }),
    [data, loading, lastChecked, refresh],
  );
  return <ProviderStatusContext.Provider value={value}>{children}</ProviderStatusContext.Provider>;
};

export const useProviderStatus = () => {
  const ctx = useContext(ProviderStatusContext);
  if (!ctx) throw new Error('useProviderStatus debe usarse dentro de ProviderStatusProvider');
  return ctx;
};

export default ProviderStatusContext;
